import { useEffect, useState } from 'react';
import { X, Paperclip, Loader2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { formatBytes } from '../lib/upload.js';

/**
 * Files something from a thread against a ticket.
 *
 * Tickets on this conversation come first, because that is almost always the
 * intent — but every open ticket is offered, since a merchant will send the
 * screenshot for one problem in the middle of talking about another. Creating
 * a ticket from here is the third option: the alternative is leaving the view,
 * making the ticket, coming back and finding the message again.
 */
export function AttachToTicketDialog({ appId, conversationId, file, onClose, onAttached }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    api.tickets
      .list(appId, { limit: 100 })
      .then(({ data }) => live && setTickets(Array.isArray(data) ? data : []))
      .catch(() => live && setTickets([]))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [appId]);

  const descriptor = { key: file.key, name: file.name, mimeType: file.mimeType, size: file.size };

  const attach = async (ticket) => {
    setBusy(ticket.id);
    setError(null);
    try {
      const updated = await api.tickets.attach(appId, ticket.id, [descriptor]);
      onAttached?.(updated);
      onClose();
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  };

  const createWithFile = async (event) => {
    event.preventDefault();
    setBusy('new');
    setError(null);
    try {
      const ticket = await api.tickets.create(appId, {
        subject,
        conversationId,
        attachments: [descriptor],
      });
      onAttached?.(ticket);
      onClose();
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  };

  // This thread's tickets first, then everything still open.
  const ordered = [
    ...tickets.filter((t) => t.conversationId === conversationId),
    ...tickets.filter(
      (t) => t.conversationId !== conversationId && !['resolved', 'closed'].includes(t.status)
    ),
  ];

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div className="dialog" onMouseDown={(e) => e.stopPropagation()} role="dialog">
        <header className="dialog__head">
          <h2>Attach to a ticket</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <div className="attach-file">
          <Paperclip size={12} />
          <span className="attach-file__name">{file.name || 'Attachment'}</span>
          {file.size > 0 && <span className="attach-file__size">{formatBytes(file.size)}</span>}
        </div>

        {error && <div className="login__error">{error}</div>}

        <ul className="ticket-picker">
          {loading && <li className="table__empty">Loading tickets…</li>}
          {!loading && ordered.length === 0 && (
            <li className="table__empty">No tickets yet — create one below.</li>
          )}
          {ordered.map((ticket) => (
            <li key={ticket.id}>
              <button
                type="button"
                className="ticket-picker__item"
                onClick={() => attach(ticket)}
                disabled={Boolean(busy)}
              >
                <span className="mono muted">SC-{ticket.number}</span>
                <span className="ticket-picker__subject">{ticket.subject}</span>
                {ticket.conversationId === conversationId && (
                  <span className="pill pill--accent">this chat</span>
                )}
                {busy === ticket.id && <Loader2 size={12} className="spin" />}
              </button>
            </li>
          ))}
        </ul>

        <form className="shortcut-new" onSubmit={createWithFile}>
          <label className="field">
            <span className="field__label">OR OPEN A NEW TICKET WITH IT</span>
            <input
              className="field__control"
              maxLength={300}
              value={subject}
              placeholder="What is this about?"
              onChange={(e) => setSubject(e.target.value)}
            />
          </label>
          <button
            type="submit"
            className="button button--primary"
            disabled={Boolean(busy) || !subject.trim()}
          >
            Create ticket
          </button>
        </form>
      </div>
    </div>
  );
}

export default AttachToTicketDialog;
